import React from 'react';
import { X, Lightbulb } from 'lucide-react';

/**
 * Not currently reachable. `showInsights` in App.js initialises to false and
 * nothing sets it true — the only setter is this panel's own onClose. It used
 * to be opened from QuickMenu, which is no longer rendered either.
 *
 * It is left in place rather than deleted, but the mock insights it used to
 * fall back on are gone. They were fabricated — "Lead #1 has 95% conversion
 * probability", "Avoid traffic on Route 7 after 4 PM" — and because App.js
 * renders this without an `insights` prop, reconnecting the entry point would
 * have shipped invented AI output to a salesperson as though it were real.
 * With no data it now says so.
 */
const TodaysInsights = ({ insights, isEmbedded = false, onClose }) => {
    const items = insights || [];
    const empty = items.length === 0;

    if (isEmbedded) {
        return (
            <div className="w-full bg-gray-800 rounded-xl shadow-sm border border-gray-700 overflow-hidden">
                <div className="p-4 space-y-3">
                    {empty && <p className="text-sm text-gray-400">No insights for today yet.</p>}
                    {items.map((insight, index) => {
                        const Icon = insight.icon;
                        const colorClasses = {
                            success: 'text-emerald-300 bg-emerald-900/30',
                            warning: 'text-orange-300 bg-orange-900/30',
                            info: 'text-blue-300 bg-blue-900/30',
                            default: 'text-gray-300 bg-gray-900/50'
                        };
                        const colors = colorClasses[insight.type] || colorClasses.default;

                        return (
                            <div
                                key={index}
                                className={`flex items-start gap-3 p-3 rounded-lg ${colors}`}
                            >
                                <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                                <p className="text-sm font-medium leading-relaxed">
                                    {insight.text}
                                </p>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    // Panel overlay version (not embedded)
    return (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-40 flex items-start justify-center p-4" onClick={onClose}>
            <div
                className="bg-gray-800 border border-gray-700 rounded-xl shadow-2xl w-full max-w-2xl mt-4 md:mt-48 overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="bg-gradient-to-r from-blue-500 to-purple-500 p-4 text-white flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Lightbulb className="w-6 h-6" />
                        <h3 className="text-lg font-bold">Today's Insights</h3>
                    </div>
                    <button aria-label="Close insights"
                        onClick={onClose}
                        className="hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                    {empty && (
                        <p className="text-sm text-gray-400 text-center py-6">
                            No insights for today yet.
                        </p>
                    )}
                    {items.map((insight, index) => {
                        const Icon = insight.icon;
                        const colorClasses = {
                            success: 'text-emerald-300 bg-emerald-900/30',
                            warning: 'text-orange-300 bg-orange-900/30',
                            info: 'text-blue-300 bg-blue-900/30',
                            default: 'text-gray-300 bg-gray-900/50'
                        };
                        const colors = colorClasses[insight.type] || colorClasses.default;

                        return (
                            <div
                                key={index}
                                className={`flex items-start gap-4 p-4 rounded-lg ${colors}`}
                            >
                                <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                                <p className="text-sm font-medium">{insight.text}</p>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default TodaysInsights;
